import supabase from "../../supabase/supabase";

const PROFILE_COLUMNS =
  "id,first_name,last_name,email,phone,avatar_url,role";

function rows(data) {
  if (Array.isArray(data)) return data;
  return data ? [data] : [];
}

function raise(error, context) {
  if (error) {
    throw new Error(`${context}: ${error.message}`);
  }
}

function displayName(profile) {
  return [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
}

async function getAuthenticatedUser() {
  const { data, error } = await supabase.auth.getUser();
  raise(error, "Unable to verify your session");
  if (!data?.user) throw new Error("Authentication required.");
  return data.user;
}

async function verifyClientMembership(organizationId, userId) {
  const { data, error } = await supabase
    .schema("accounting")
    .from("organization_users")
    .select("organization_id,user_id,role")
    .eq("organization_id", organizationId)
    .eq("user_id", userId)
    .maybeSingle();

  raise(error, "Unable to verify access to this business");
  if (!data) throw new Error("You are not authorized to view this business.");
  return data;
}

export async function getClientManagingFirm(organizationId) {
  const user = await getAuthenticatedUser();
  await verifyClientMembership(organizationId, user.id);

  const { data: profile, error: profileError } = await supabase
    .schema("accounting")
    .from("client_profiles")
    .select(
      "organization_id,company_name,managing_organization_id,bookkeeping_manager,business_email,phone"
    )
    .eq("organization_id", organizationId)
    .maybeSingle();

  raise(profileError, "Unable to load this business profile");

  if (!profile?.managing_organization_id) {
    return { firm: null, clientProfile: profile || null };
  }

  const { data: firm, error: firmError } = await supabase
    .schema("accounting")
    .from("organizations")
    .select("id,name,type,website,country,created_at")
    .eq("id", profile.managing_organization_id)
    .in("type", ["accounting_firm", "accountant_practice"])
    .maybeSingle();

  raise(firmError, "Unable to load the managing accounting firm");

  return {
    firm: firm || null,
    clientProfile: profile,
  };
}

export async function getClientAssignedAccountants(
  organizationId,
  managingOrganizationId
) {
  if (!organizationId || !managingOrganizationId) return [];

  const user = await getAuthenticatedUser();
  await verifyClientMembership(organizationId, user.id);

  const { data: assignmentRows, error: assignmentError } =
    await supabase
      .schema("accounting")
      .from("client_assignments")
      .select(
        "id,managing_organization_id,client_organization_id,user_id,assigned_by,is_active,created_at"
      )
      .eq("client_organization_id", organizationId)
      .eq("managing_organization_id", managingOrganizationId)
      .eq("is_active", true)
      .order("created_at", { ascending: true })
      .limit(50);

  raise(assignmentError, "Unable to load assigned accountants");

  const assignments = rows(assignmentRows);
  if (!assignments.length) return [];

  const userIds = [...new Set(assignments.map((item) => item.user_id).filter(Boolean))];
  if (!userIds.length) return [];

  const { data: memberships, error: membershipError } = await supabase
    .schema("accounting")
    .from("organization_users")
    .select("organization_id,user_id,role")
    .eq("organization_id", managingOrganizationId)
    .in("user_id", userIds);

  raise(membershipError, "Unable to verify accounting team membership");

  const validMemberships = new Map(
    rows(memberships).map((membership) => [
      membership.user_id,
      membership,
    ])
  );
  const verifiedAssignments = assignments;

  const verifiedUserIds = [
    ...new Set(verifiedAssignments.map((assignment) => assignment.user_id)),
  ];
  if (!verifiedUserIds.length) return [];

  const { data: profiles, error: profileError } = await supabase
    .from("profiles")
    .select(PROFILE_COLUMNS)
    .in("id", verifiedUserIds);

  const profileAccessLimited = Boolean(profileError);
  const profileById = new Map(
    rows(profileError ? [] : profiles).map((profile) => [
      profile.id,
      profile,
    ])
  );

  return verifiedAssignments.map((assignment) => {
    const profile = profileById.get(assignment.user_id);
    const membership = validMemberships.get(assignment.user_id);

    return {
      id: assignment.id,
      userId: assignment.user_id,
      name: displayName(profile) || "Assigned team member",
      role: membership?.role || "Assigned accountant",
      email: profile?.email || "",
      phone: profile?.phone || "",
      avatarUrl: profile?.avatar_url || "",
      createdAt: assignment.created_at,
      profileAccessLimited:
        profileAccessLimited || !profile,
      membershipAccessLimited: !membership,
    };
  });
}

export async function getClientAccountingTeam(
  organizationId,
  managingOrganizationId
) {
  return getClientAssignedAccountants(
    organizationId,
    managingOrganizationId
  );
}

export async function getClientAccountantOverview(organizationId) {
  if (!organizationId) throw new Error("An active business is required.");

  const { firm, clientProfile } =
    await getClientManagingFirm(organizationId);

  const team = firm
    ? await getClientAccountingTeam(organizationId, firm.id)
    : [];

  return {
    firm,
    clientProfile,
    team,
    assignedAccountant: team.length === 1 ? team[0] : null,
    multipleAssignments: team.length > 1,
    profileAccessLimited: team.some(
      (member) => member.profileAccessLimited
    ),
    membershipAccessLimited: team.some(
      (member) => member.membershipAccessLimited
    ),
  };
}